import { useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { DATA_HEALTH } from '@elementary/common-ui/constants/queryKeys';
import { DataHealthDashboard } from '@elementary/common-ui/services/gql/types';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { getTreeNodeAssetIds } from '@elementary/common-ui/hooks/treeData/utils';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import { getDataHealthDashboardQuery } from '../services/api';
import { QualityDimension } from '../types';
import useDataHealthFilters from './useDataHealthFilters';

const useDataHealth = () => {
  const selectedEnvId = useSelectedEnv();
  const { daysBack, urlFilters } = useDataHealthFilters();
  const { selectedTreeNode, selectedTreeFolderNodes } = useSelectedTreeItem();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.flatMap(getTreeNodeAssetIds);
    }

    if (selectedTreeNode) {
      return getTreeNodeAssetIds(selectedTreeNode);
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const selectedTreeNodesHash = useMemo(
    () => selectedTreeNodeIds && hashArray(selectedTreeNodeIds),
    [selectedTreeNodeIds],
  );

  const {
    data: response,
    isFetching,
    isFetched,
    error,
    refetch,
  } = useGraphqlQuery<{ dataHealthDashboard: DataHealthDashboard }>({
    queryKey: [
      DATA_HEALTH,
      selectedEnvId,
      daysBack,
      urlFilters,
      selectedTreeNodesHash,
    ],
    query: getDataHealthDashboardQuery,
    variables: {
      assetIds: selectedTreeNodeIds,
      daysBack: Number(daysBack),
      filters: urlFilters && urlFilters.length > 0 ? urlFilters : undefined,
    },
  });

  const data = response?.dataHealthDashboard;

  // Transform GraphQL response to match the existing component expectations
  const transformedData = useMemo(() => {
    if (!data) return undefined;

    // Convert qualityDimensions array to Record format
    const qualityDimensionsRecord = data.qualityDimensions.reduce(
      (acc: Record<string, QualityDimension>, dim) => {
        acc[dim.dimension] = dim;
        return acc;
      },
      {},
    );

    return {
      ...data,
      qualityDimensions: qualityDimensionsRecord,
    };
  }, [data]);

  return {
    data: transformedData,
    isFetching,
    isFetched,
    error,
    refetch,
  };
};

export default useDataHealth;
